<template>
  <div class="container-full-screen">
    <div class="container-control-screen">
      <div class="wrapper-actions">
        <p class="small accent dim-dark">{{ t('label_language') }}</p>
        <ButtonDropdownLanguage v-model="preference.languageCode" chat-type="SCREEN" @select="selectLanguage" />
        <div class="divider-small" />
      </div>

      <div class="wrapper-features">
        <p class="small accent dim-dark">{{ t('label_text_control') }}</p>
        <Gauge
          v-model="preference.textSizeMultiplier"
          v-bind="SCREEN_PREFERENCE_GAUGE_PRESET.textSizeMultiplier"
          :title="`${t('text_size')}: ${preference.textSizeMultiplier}`"
        />
        <div class="divider-small" />
      </div>

      <div class="wrapper-features">
        <p class="small accent dim-dark">{{ t('screen_area_control') }}</p>
        <Gauge
          v-model="preference.screenScrollHeight"
          v-bind="SCREEN_PREFERENCE_GAUGE_PRESET.screenScrollHeight"
          :title="`${t('screen_mode_message_height')}: ${preference.screenScrollHeight}`"
        />

        <NumericInput
          v-model="preference.screenMessageWidth"
          :title="t('screen_mode_message_width')"
          :min="validationRules.screenMessageWidth.minimum"
          :max="validationRules.screenMessageWidth.maximum"
          :validate="validationRules.screenMessageWidth.validate"
          unit="%"
          using-button
        />

        <NumericInput
          v-if="isVisibleControllerHeight"
          v-model="preference.screenContainerHeight"
          :title="t('screen_mode_container_height')"
          :min="validationRules.screenContainerHeight.minimum"
          :max="validationRules.screenContainerHeight.maximum"
          :validate="validationRules.screenContainerHeight.validate"
          unit="px"
          using-button
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import ButtonDropdownLanguage from '@/components/domains/chat/common/ButtonDropdownLanguage.vue';
import Gauge from '@/components/shared/base/Gauge.vue';
import NumericInput from '@/components/shared/composite/NumericInput.vue';
import { useChatPreferences } from '@/composables/shared/useChatPreferences';
import { useCallStore } from '@/stores/call';
import { useSocketStore } from '@/stores/socket';
import { LanguageCode } from '@/types/common';
import { ChatPreferenceStorageType, ScreenPreference } from '@/types/components';
import { getScreenPreference, SCREEN_PREFERENCE_GAUGE_PRESET } from '@/utils/constants/chatPreference';

const preference = defineModel<ScreenPreference>({
  required: true,
  default: () => getScreenPreference(),
});

const props = withDefaults(
  defineProps<{
    type: ChatPreferenceStorageType;
  }>(),
  {
    type: 'remote',
  }
);

const callStore = useCallStore();
const socketStore = useSocketStore();

const { validationRules } = useChatPreferences();

const { t } = useI18n();

// 스크린 모드 고정된 화면 높이 값 조절 기능 보임 여부
const isVisibleControllerHeight = computed<boolean>(() => {
  return !!callStore.qrCodeInfo.options.screenHeight;
});

const selectLanguage = (languageCode: LanguageCode) => {
  if (props.type === 'local') {
    socketStore.sendMessageChangeLanguage(languageCode);
  }
};
</script>

<style scoped lang="scss">
.container-full-screen {
  width: 100%;
}

.container-control-screen {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
}

.wrapper-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
}

.wrapper-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
